import React, { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { createClient } from '@supabase/supabase-js';

const supabase = createClient(
  'https://gagtvzlfkwkvipnaojku.supabase.co',
  'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImdhZ3R2emxma3drdmlwbmFvamt1Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzM5MDU2OTQsImV4cCI6MjA4OTQ4MTY5NH0.-pzFR6gXznbcOT9kmbDElCtAAoLDChYYBzm4rxG_6UY'
);

const AIEvolutionGame = () => {
  const [currentStep, setCurrentStep] = useState('welcome');
  const [responses, setResponses] = useState({});
  const [archetype, setArchetype] = useState(null);
  const [allResults, setAllResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Auto-refresh dashboard every 15 seconds
  useEffect(() => {
    if (currentStep !== 'dashboard') return;
    const interval = setInterval(() => {
      loadResults();
    }, 15000);
    return () => clearInterval(interval);
  }, [currentStep]);

  const questions = [
    {
      id: 'q1', weight: 2,
      title: 'Je krijgt toegang tot een nieuwe AI-tool. Wat doe je?',
      options: [
        { animal: 'vos', label: 'Ik test meteen wat er technisch kan.' },
        { animal: 'uil', label: 'Ik wil eerst snappen hoe het model werkt.' },
        { animal: 'das', label: 'Ik kijk hoe dit past in ons werkproces.' },
        { animal: 'eekhoorn', label: 'Ik probeer van alles uit en zie wel wat er gebeurt.' },
        { animal: 'hert', label: 'Ik denk na over wat dit betekent voor mij en mijn team.' }
      ]
    },
    {
      id: 'q2', weight: 1,
      title: 'Hoe formuleert een model als ChatGPT een antwoord?',
      options: [
        { animal: 'vos', label: 'Het combineert patronen tot een logisch klinkend antwoord.' },
        { animal: 'uil', label: 'Het voorspelt telkens het meest waarschijnlijke volgende woord.' },
        { animal: 'das', label: 'Het werkt binnen vooraf ingestelde systeemregels.' },
        { animal: 'eekhoorn', label: 'Het past zich flexibel aan jouw input aan.' },
        { animal: 'hert', label: 'Het verwerkt taal zonder echt begrip of bewustzijn.' }
      ]
    },
    {
      id: 'q3', weight: 2,
      title: 'Het antwoord dat de AI-tool geeft klopt niet helemaal. Wat doe je?',
      options: [
        { animal: 'vos', label: 'Ik scherp mijn prompt aan en probeer het opnieuw.' },
        { animal: 'uil', label: 'De output klopt bij mij vrijwel altijd.' },
        { animal: 'das', label: 'Ik bouw zo snel als mogelijk een custom GPT om dit voortaan te voorkomen.' },
        { animal: 'eekhoorn', label: 'Ik ga het gesprek aan met AI en test welke prompt het beste werkt.' },
        { animal: 'hert', label: 'Ik verbeter de output zelf.' }
      ]
    },
    {
      id: 'q4', weight: 1,
      title: 'Wat is het grootste risico van generatieve AI?',
      options: [
        { animal: 'vos', label: 'Dat de output technisch onvoorspelbaar is.' },
        { animal: 'uil', label: 'Dat het overtuigend onjuiste informatie geeft.' },
        { animal: 'das', label: 'Dat niemand duidelijke afspraken maakt over gebruik.' },
        { animal: 'eekhoorn', label: 'Dat we blijven experimenteren zonder richting.' },
        { animal: 'hert', label: 'Dat mensen niet begrijpen hoe een uitkomst tot stand komt.' }
      ]
    },
    {
      id: 'q5', weight: 2,
      title: 'Wat is voor jou het voordeel van het gebruik van AI-tools?',
      options: [
        { animal: 'vos', label: 'Om slimmer te werken met betere prompts en scherpere output.' },
        { animal: 'uil', label: 'Voor het uitvoeren van complexe taken zoals research, content of analyses.' },
        { animal: 'das', label: 'Vooral dat ik mijn werk sneller en efficiënter doe.' },
        { animal: 'eekhoorn', label: 'Voor inspiratie of om snel iets te controleren.' },
        { animal: 'hert', label: 'Om bestaande content zorgvuldiger en duidelijker te maken.' }
      ]
    },
    {
      id: 'q6', weight: 1,
      title: 'Wanneer spreek je van volwassen AI-adoptie?',
      options: [
        { animal: 'vos', label: 'Als AI slim wordt ingezet om werk te versnellen.' },
        { animal: 'uil', label: 'Als leiders begrijpen hoe AI-modellen werken.' },
        { animal: 'das', label: 'Als AI goed is ingebed in werkprocessen met duidelijke afspraken.' },
        { animal: 'eekhoorn', label: 'Als experimenteren onderdeel is van de cultuur.' },
        { animal: 'hert', label: 'Als mens en technologie in balans zijn.' }
      ]
    },
    {
      id: 'q7', weight: 2,
      title: 'Wat typeert jouw manier van werken?',
      options: [
        { animal: 'vos', label: 'Actiegericht en snel; wat moet er gebeuren? Go!' },
        { animal: 'uil', label: 'Eerst begrijpen, dán uitvoeren.' },
        { animal: 'das', label: 'Plannen en to-do-lijstjes zijn geweldig.' },
        { animal: 'eekhoorn', label: 'Ik leer door te doen en veel te experimenteren.' },
        { animal: 'hert', label: 'Teamwork makes the dream work!' }
      ]
    },
    {
      id: 'q8', weight: 1,
      title: 'Hoe ziet jouw AI-gebruik eruit?',
      options: [
        { animal: 'das', label: 'Ik wacht tot ik een licentie en instructies krijg van mijn werkgever.' },
        { animal: 'hert', label: 'Privé al veel, op werk nog weinig.' },
        { animal: 'vos', label: 'Ik heb één tool die ik gebruik.' },
        { animal: 'eekhoorn', label: 'Ik ben al jarenlang meerdere tools aan het testen.' },
        { animal: 'uil', label: 'Ik ben vooral op één toepassing de diepte in gegaan.' }
      ]
    },
    {
      id: 'q9', weight: 2,
      title: 'Wat vind jij het belangrijkste bij AI?',
      options: [
        { animal: 'vos', label: 'Dat het technisch goed werkt.' },
        { animal: 'uil', label: 'Dat het inhoudelijk klopt.' },
        { animal: 'das', label: 'Dat het duidelijk en overzichtelijk is.' },
        { animal: 'eekhoorn', label: 'Dat het vernieuwend is.' },
        { animal: 'hert', label: 'Dat het verantwoord gebeurt.' }
      ]
    },
    {
      id: 'q10', weight: 1,
      title: 'Wat onderscheidt generatieve AI van traditionele software?',
      options: [
        { animal: 'vos', label: 'Het werkt met waarschijnlijkheid in plaats van vaste regels.' },
        { animal: 'uil', label: 'Het is een statistisch voorspelmodel.' },
        { animal: 'das', label: 'Het ondersteunt processen zonder vaste scripts.' },
        { animal: 'eekhoorn', label: 'Het past zich flexibel aan nieuwe input aan.' },
        { animal: 'hert', label: 'Het genereert output zonder echt begrip.' }
      ]
    }
  ];

  const archetypes = {
    vos: {
      name: 'De Vos',
      icon: '🦊',
      color: '#FF7066',
      subtitle: 'Pragmatisch & technisch',
      tagline: 'Slim en wendbaar',
      kenmerken: [
        { title: 'Oplossingsgericht', text: 'Je denkt in oplossingen, niet in problemen.' },
        { title: 'Technisch', text: 'Je optimaliseert liever meteen dan dat je blijft nadenken.' },
        { title: 'Pragmatisch', text: 'AI is geen mysterie maar gereedschap om sneller en efficiënter te werken.' }
      ],
      description: 'De vos is snel en wendbaar. Net als jij.',
      feedback: 'Daar zit juist je kracht. Jij ziet snel wat er kan. Waar anderen nog aftasten, ben jij al aan het testen. Je denkt in oplossingen, in slimme prompts. En daarbij houd je het tempo er flink in. Met verbeteren en finetunen tot het klopt. Je bent altijd op zoek naar dé formulering waardoor de output ineens twee keer zo goed wordt. AI is voor jou geen magie. Het is techniek. Iets dat je doelgericht inzet om slimmer te werken. En techniek kun je verbeteren. Werkt iets niet? Dan pas je het aan.',
      nextStep: 'Wil je je voorsprong vergroten? Dan is verdieping in prompting, modelgedrag en AI-toepassing op businessvraagstukken interessant voor je. Denk aan een opleiding waarin je techniek koppelt aan organisatiebrede impact:',
      opleidingen: [
        { title: 'AI in Business', url: 'https://www.beeckestijn.org/opleidingen/ai-in-business-masterclass/' },
        { title: 'AI Business Professional', url: 'https://www.beeckestijn.org/opleidingen/ai-business-professional-post-hbo/' },
        { title: 'AI Powered Online Marketeer', url: 'https://www.beeckestijn.org/opleidingen/ai-powered-online-marketeer-post-hbo/' },
        { title: 'Digital Marketing Professional', url: 'https://www.beeckestijn.org/opleidingen/digital-ai-marketing-professional-post-hbo/' }
      ]
    },
    uil: {
      name: 'De Uil',
      icon: '🦉',
      color: '#6366f1',
      subtitle: 'Verdiepend modelbegrip',
      tagline: 'Analytisch en kritisch',
      kenmerken: [
        { title: 'Onderzoekend', text: 'Je wilt begrijpen vóórdat je toepast.' },
        { title: 'Doorgrondend', text: 'Je stelt vragen waar anderen overheen stappen.' },
        { title: 'Kritisch', text: 'Je vertrouwt output pas als je de logica ziet.' }
      ],
      description: '"Interessant… maar hoe werkt dit precies?"',
      feedback: 'Dat zou zomaar jouw standaardreactie kunnen zijn. Jij wilt snappen hoe het model tot een antwoord komt. Je bent nieuwsgierig naar de onderliggende logica. Hoe komt een antwoord tot stand? Waar zit de beperking? Wat gebeurt er achter de schermen? Je stelt vragen waar anderen overheen stappen. Je analyseert. Je reflecteert. En soms zucht je zacht als iemand zegt: "Het werkt toch gewoon?" Maar dankzij jou blijft iedereen scherp. Dat maakt je waardevol in elk AI-gesprek.',
      nextStep: 'Voor jou ligt de groei in het vertalen van je inzicht naar toepassing. Hoe maak je van analyse ook beweging? Een opleiding die strategie en implementatie combineert kan je helpen om impact te vergroten.',
      opleidingen: [
        { title: 'Digital Marketing Strategie & Leiderschap', url: 'https://www.beeckestijn.org/opleidingen/digital-marketing-strategie-ai-leiderschap-masterclass/' },
        { title: 'AI Business Professional', url: 'https://www.beeckestijn.org/opleidingen/ai-business-professional-post-hbo/' },
        { title: 'AI Agents & Automations', url: 'https://www.beeckestijn.org/opleidingen/ai-agents-automations-training/' },
        { title: 'Vibe Coding', url: 'https://www.beeckestijn.org/opleidingen/vibe-coding-introductie-training/' }
      ]
    },
    das: {
      name: 'De Das',
      icon: '🦡',
      color: '#012F54',
      subtitle: 'Procesmatig inzicht',
      tagline: 'Bouwer en structuurdenker',
      kenmerken: [
        { title: 'Procesgericht', text: 'Je denkt in processen en afspraken.' },
        { title: 'Vooruitziend', text: 'Je ziet kansen en risico\'s voordat ze zichtbaar worden.' },
        { title: 'Duurzaam', text: 'Je bouwt liever iets langer aan iets dat toekomstbestendig is dan dat je blijft aanmodderen.' }
      ],
      description: 'Waar anderen enthousiast roepen: "We moeten iets met AI!", denk jij: "Mooi. Wie is verantwoordelijk? En hoe borgen we dit?"',
      feedback: 'Jij denkt niet alleen: wat kan AI? Maar vooral: hoe regelen we dit goed? Je kijkt naar structuur. Naar schaalbaarheid. Naar wat er gebeurt als het experiment succesvol wordt. Je hecht waarde aan processen, afspraken en verantwoordelijkheden. Je wilt dat iets blijft werken. Niet alleen vandaag, maar ook volgend jaar.',
      nextStep: 'Wil je je rol verder versterken? Dan past een opleiding rond AI-governance, implementatie of digitale transformatie goed bij je. Zo maak je van structuur ook strategisch voordeel.',
      opleidingen: [
        { title: 'AI Adoptie, Cultuur & Governance', url: 'https://www.beeckestijn.org/opleidingen/ai-adoptie-cultuur-governance-masterclass/' },
        { title: 'Digital Marketing Strategie & Leiderschap', url: 'https://www.beeckestijn.org/opleidingen/digital-marketing-strategie-ai-leiderschap-masterclass/' },
        { title: 'Bouw je Digitale AI-Team', url: 'https://www.beeckestijn.org/opleidingen/bouw-jouw-digitale-ai-team-training/' },
        { title: 'AI in Business', url: 'https://www.beeckestijn.org/opleidingen/ai-in-business-masterclass/' }
      ]
    },
    eekhoorn: {
      name: 'De Eekhoorn',
      icon: '🐿️',
      color: '#10b981',
      subtitle: 'Experimenteel',
      tagline: 'Nieuwsgierig en energiek',
      kenmerken: [
        { title: 'Energiek', text: 'Je krijgt energie van nieuwe tools.' },
        { title: 'Experimenteel', text: 'Je leert door te proberen.' },
        { title: 'Vooruitstrevend', text: 'Je ziet kansen voordat ze zijn uitgewerkt.' }
      ],
      description: 'Nieuwe AI-update? Jij hebt hem al getest. Nieuwe tool? Pff, oud nieuws!',
      feedback: 'Je houdt van ontdekken. Van kijken wat er gebeurt als je iets nét op een andere manier prompt. Je hebt standaard tien tabbladen open waarbij je snel kunt schakelen tussen de verschillende modellen. Want voor elk taakje gebruik jij dé perfecte tool. Je denkt minder in beperkingen en meer in mogelijkheden. Soms spring je van idee naar idee. Jij houdt van die snelheid, maar soms ben je té snel en te vooruitstrevend voor anderen. Juist daardoor zie jij verbanden die anderen missen.',
      nextStep: 'Wil je je experimenten meer richting geven? Dan helpt het om je kennis van tools en toepassingen te verdiepen. Zo maak je van jouw enthousiasme en nieuwsgierigheid een duurzame kracht.',
      opleidingen: [
        { title: 'Agents & Automations', url: 'https://www.beeckestijn.org/opleidingen/ai-agents-automations-training/' },
        { title: 'Vibe Coding', url: 'https://www.beeckestijn.org/opleidingen/vibe-coding-introductie-training/' },
        { title: 'AI Adoptie, Cultuur & Governance', url: 'https://www.beeckestijn.org/opleidingen/ai-adoptie-cultuur-governance-masterclass/' },
        { title: 'AI Prompting & Tools', url: 'https://www.beeckestijn.org/opleidingen/ai-prompting-tools-training/' }
      ]
    },
    hert: {
      name: 'Het Hert',
      icon: '🦌',
      color: '#88E6FF',
      subtitle: 'Verbindend & mensgericht',
      tagline: 'Bewust en reflectief',
      kenmerken: [
        { title: 'Mensgericht', text: 'Je kijkt naar de impact op mensen, teams en de organisatie.' },
        { title: 'Bewust', text: 'Je denkt na over hoe je AI verantwoord inzet.' },
        { title: 'Verbindend', text: 'Jij wil het beste halen uit technologie én de mens.' }
      ],
      description: 'Voor jou draait AI niet alleen om technologie. Het gaat ook om mensen.',
      feedback: 'Je vraagt je af: wat betekent dit voor mijn team? Voor klanten? Voor vertrouwen? Je kijkt verder dan efficiëntie. Je ziet de menselijke kant. En kijkt daarbij naar de impact op het geheel. In gesprekken over AI breng jij nuance. Je zoomt uit en bent vaak degene in gesprekken die even op de rem drukt. Maar dit betekent niet dat jij geen voorstander bent van verandering! Jij kijkt alleen nét iets verder en wilt weten: hoe kunnen mens en technologie naast elkaar bestaan en elkaar versterken? Dat scherp hebben én houden, daarin zit jouw kracht. Zo kun je er namelijk voor zorgen dat AI écht door iedereen wordt omarmd.',
      nextStep: 'Wil je jouw verbindende rol versterken? Dan is een opleiding over AI en leiderschap een logische volgende stap. Zo help je organisaties niet alleen slimmer, maar ook bewuster werken met aandacht voor de mens én de techniek.',
      opleidingen: [
        { title: 'Digital Marketing Strategie & Leiderschap', url: 'https://www.beeckestijn.org/opleidingen/digital-marketing-strategie-ai-leiderschap-masterclass/' },
        { title: 'AI Business Professional', url: 'https://www.beeckestijn.org/opleidingen/ai-business-professional-post-hbo/' },
        { title: 'AI Adoptie, Cultuur & Governance', url: 'https://www.beeckestijn.org/opleidingen/ai-adoptie-cultuur-governance-masterclass/' },
        { title: 'AI in Business', url: 'https://www.beeckestijn.org/opleidingen/ai-in-business-masterclass/' }
      ]
    }
  };

  const getArchetype = (responses) => {
    const scores = { vos: 0, uil: 0, das: 0, eekhoorn: 0, hert: 0 };
    for (const [questionId, animal] of Object.entries(responses)) {
      const question = questions.find(q => q.id === questionId);
      if (question) {
        scores[animal] = (scores[animal] || 0) + question.weight;
      }
    }
    let maxAnimal = 'vos';
    let maxScore = 0;
    for (const [animal, score] of Object.entries(scores)) {
      if (score > maxScore) {
        maxScore = score;
        maxAnimal = animal;
      }
    }
    return { archetype: maxAnimal, scores };
  };

const loadResults = async () => {
  setLoading(true);
  try {
    const { data } = await supabase.from('responses').select('archetype');
    const results = { vos: 0, uil: 0, das: 0, eekhoorn: 0, hert: 0, totalResponses: data.length };
    data.forEach(row => { if (row.archetype) results[row.archetype]++; });
    setAllResults(results);
  } catch {
    setAllResults({ vos: 0, uil: 0, das: 0, eekhoorn: 0, hert: 0, totalResponses: 0 });
  } finally {
    setLoading(false);
  }
};

  const handleResetData = async () => {
  setShowResetConfirm(false);
  setLoading(true);
  try {
    await supabase.from('responses').delete().neq('id', '00000000-0000-0000-0000-000000000000');
    await loadResults();
  } catch (error) {} finally { setLoading(false); }
};

  const handleSelect = (questionId, animal) => {
    setResponses({ ...responses, [questionId]: animal });
  };

  const handleNext = (currentIndex) => {
    if (currentIndex < questions.length - 1) {
      setCurrentStep(`question-${currentIndex + 1}`);
    } else {
      handleSubmit();
    }
  };

  const handleSubmit = async () => {
  if (isSubmitting) return;
  setIsSubmitting(true);
  const result = getArchetype(responses);
  setArchetype(result.archetype);
  setCurrentStep('result');
  try {
    await supabase.from('responses').insert({
      archetype: result.archetype,
      scores: result.scores
    });
  } catch (e) {}
  setIsSubmitting(false);
};

  const currentQuestionIndex = currentStep.startsWith('question-') ? parseInt(currentStep.split('-')[1]) : 0;

  // Welcome Screen
  if (currentStep === 'welcome') {
    return (
      <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #012F54 0%, #88E6FF 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px', fontFamily: 'Arial, sans-serif' }}>
        <div style={{ background: 'white', borderRadius: '20px', padding: '28px', maxWidth: '560px', width: '100%', boxShadow: '0 20px 60px rgba(1, 47, 84, 0.3)', textAlign: 'center' }}>
       <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '10px', marginTop: '-28px', marginRight: '-10px' }}>

  <img 
    src="/beeckestijn-logo- label-boven (1).png" 
    alt="Beeckestijn Business School" 
    style={{ height: '60px', width: 'auto' }} 
  />
</div>
          <h1 style={{ fontSize: '20px', color: '#FF7066', marginBottom: '10px', fontWeight: '600' }}>AI Archetype Test</h1>
          <h3 style={{ fontSize: '15px', color: '#012F54', marginBottom: '10px', fontWeight: '600' }}>Ontdek jouw AI-archetype in 3-4 minuten</h3>
          <p style={{ fontSize: '13px', color: '#4a5568', marginBottom: '8px', lineHeight: '1.6', textAlign: 'left' }}>
            Stel je de wereld van AI voor als een bos. Een levendig ecosysteem waarin ieder dier zijn eigen rol heeft. De één is alert en jaagt graag. De ander kijkt rond en houdt de boel in de gaten. Sommige dieren bouwen aan structuur, terwijl anderen in groepsverband bewegen.
          </p>
          <p style={{ fontSize: '13px', color: '#012F54', marginBottom: '8px', lineHeight: '1.6', fontWeight: '600' }}>Hoe zit dat bij jou?</p>
          <p style={{ fontSize: '13px', color: '#4a5568', marginBottom: '18px', lineHeight: '1.6', textAlign: 'left' }}>
            Beantwoord de vragen intuïtief en kies het antwoord dat het meest bij je past. Aan het einde zie je welk dier jouw AI-werkstijl het beste typeert.
          </p>
          <button
            onClick={() => { setResponses({}); setArchetype(null); setCurrentStep('question-0'); }}
            style={{ background: '#012F54', color: 'white', border: 'none', padding: '13px 36px', fontSize: '16px', borderRadius: '12px', cursor: 'pointer', fontWeight: '600', boxShadow: '0 4px 12px rgba(1, 47, 84, 0.3)', transition: 'transform 0.2s' }}
            onMouseOver={(e) => e.target.style.transform = 'translateY(-2px)'}
            onMouseOut={(e) => e.target.style.transform = 'translateY(0)'}
          >
            Start de Game
          </button>

        </div>
      </div>
    );
  }

  // Dashboard Screen
  if (currentStep === 'dashboard') {
    const chartData = allResults && allResults.totalResponses > 0 ?
      Object.entries(archetypes).map(([key, arch]) => ({
        name: arch.name, shortName: arch.name.replace('De ', '').replace('Het ', ''),
        aantal: allResults[key] || 0,
        percentage: ((allResults[key] || 0) / allResults.totalResponses * 100).toFixed(1),
        fill: arch.color
      })) : [];
    const donutData = chartData.filter(item => item.aantal > 0);

    return (
      <div style={{ minHeight: '100vh', background: '#f0f4f8', padding: '40px 20px', fontFamily: 'Arial, sans-serif' }}>
        <div style={{ maxWidth: '900px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '12px' }}>
            <h1 style={{ fontSize: '40px', color: '#012F54', fontWeight: '800', marginBottom: '8px' }}>Collectief Inzicht</h1>
            <p style={{ fontSize: '16px', color: '#718096', marginBottom: '32px' }}>Een anoniem overzicht van de AI-stijlen binnen deze groep.</p>
          </div>

          {loading && <div style={{ textAlign: 'center', padding: '80px 20px', fontSize: '18px', color: '#4a5568' }}>Resultaten laden...</div>}

          {!loading && allResults && allResults.totalResponses === 0 && (
            <div style={{ textAlign: 'center', padding: '80px 20px', background: 'white', borderRadius: '20px', boxShadow: '0 2px 16px rgba(1, 47, 84, 0.06)' }}>
              <div style={{ fontSize: '64px', marginBottom: '16px' }}>🌲</div>
              <p style={{ fontSize: '20px', color: '#012F54', fontWeight: '700', marginBottom: '8px' }}>Nog geen resultaten beschikbaar</p>
              <p style={{ fontSize: '16px', color: '#718096' }}>Wees de eerste om de evaluatie in te vullen!</p>
            </div>
          )}

          {!loading && allResults && allResults.totalResponses > 0 && (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginBottom: '24px' }}>
                <div style={{ background: 'white', borderRadius: '20px', padding: '32px 24px 20px', boxShadow: '0 2px 16px rgba(1, 47, 84, 0.06)', borderTop: '4px solid #FF7066' }}>
                  <h2 style={{ fontSize: '14px', color: '#012F54', fontWeight: '800', textTransform: 'uppercase', letterSpacing: '1.5px', marginBottom: '24px', textAlign: 'center' }}>Verdeling per Dier</h2>
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart data={chartData} barCategoryGap="20%">
                      <XAxis dataKey="shortName" axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: '#012F54', fontWeight: 600 }} height={40} interval={0} />
                      <YAxis hide />
                      <Tooltip cursor={{ fill: 'rgba(1,47,84,0.04)' }} content={({ active, payload }) => {
                        if (active && payload && payload.length) {
                          return (<div style={{ background: '#012F54', padding: '10px 14px', borderRadius: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}>
                            <p style={{ fontWeight: '700', color: 'white', fontSize: '13px', marginBottom: '2px' }}>{payload[0].payload.name}</p>
                            <p style={{ color: '#88E6FF', fontSize: '12px' }}>{payload[0].value} ({payload[0].payload.percentage}%)</p>
                          </div>);
                        } return null;
                      }} />
                      <Bar dataKey="aantal" radius={[4, 4, 0, 0]}>
                        {chartData.map((entry, index) => (<Cell key={`bar-${index}`} fill={entry.fill} />))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                <div style={{ background: 'white', borderRadius: '20px', padding: '32px 24px 20px', boxShadow: '0 2px 16px rgba(1, 47, 84, 0.06)', borderTop: '4px solid #88E6FF' }}>
                  <h2 style={{ fontSize: '14px', color: '#012F54', fontWeight: '800', textTransform: 'uppercase', letterSpacing: '1.5px', marginBottom: '24px', textAlign: 'center' }}>Aandeel (%)</h2>
                  <ResponsiveContainer width="100%" height={220}>
                    <PieChart>
                      <Pie data={donutData} cx="50%" cy="50%" innerRadius={55} outerRadius={90} paddingAngle={3} dataKey="aantal" strokeWidth={0}>
                        {donutData.map((entry, index) => (<Cell key={`cell-${index}`} fill={entry.fill} />))}
                      </Pie>
                      <Tooltip content={({ active, payload }) => {
                        if (active && payload && payload.length) {
                          return (<div style={{ background: '#012F54', padding: '10px 14px', borderRadius: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}>
                            <p style={{ fontWeight: '700', color: 'white', fontSize: '13px', marginBottom: '2px' }}>{payload[0].payload.name}</p>
                            <p style={{ color: '#88E6FF', fontSize: '12px' }}>{payload[0].payload.percentage}%</p>
                          </div>);
                        } return null;
                      }} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </div>

              <div style={{ background: 'white', borderRadius: '20px', padding: '36px 40px', boxShadow: '0 2px 16px rgba(1, 47, 84, 0.06)', borderBottom: '4px solid #FF7066', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '32px', marginBottom: '24px' }}>
                <div style={{ flex: 1 }}>
                  <p style={{ fontSize: '11px', fontWeight: '800', color: '#FF7066', textTransform: 'uppercase', letterSpacing: '2px', marginBottom: '8px' }}>Groepsreflectie</p>
                  <h2 style={{ fontSize: '26px', color: '#012F54', fontWeight: '800', marginBottom: '12px' }}>Het bos heeft iedereen nodig</h2>
                  <p style={{ fontSize: '15px', color: '#4a5568', lineHeight: '1.7' }}>
                    Met <strong>{allResults.totalResponses}</strong> deelnemers zien we een rijke variëteit aan AI-stijlen. Elke stijl draagt bij aan de gezamenlijke innovatiekracht van het team.
                  </p>
                </div>
                <div style={{ background: '#f0f4f8', borderRadius: '16px', padding: '24px 32px', textAlign: 'center', flexShrink: 0 }}>
                  <div style={{ fontSize: '48px', fontWeight: '800', color: '#012F54', lineHeight: 1 }}>{allResults.totalResponses}</div>
                  <div style={{ fontSize: '11px', fontWeight: '700', color: '#718096', textTransform: 'uppercase', letterSpacing: '2px', marginTop: '6px' }}>Deelnemers</div>
                </div>
              </div>

              <div style={{ display: 'flex', justifyContent: 'center', gap: '20px', flexWrap: 'wrap', marginBottom: '32px' }}>
                {Object.entries(archetypes).map(([key, arch]) => (
                  <div key={key} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: arch.color }} />
                    <span style={{ fontSize: '12px', color: '#4a5568', fontWeight: '600' }}>{arch.icon} {arch.name}</span>
                  </div>
                ))}
              </div>
            </>
          )}

          <div style={{ display: 'flex', justifyContent: 'center', gap: '12px', marginTop: '8px', flexWrap: 'wrap' }}>
            <button onClick={() => { setResponses({}); setArchetype(null); setCurrentStep('question-0'); }}
              style={{ background: '#FF7066', color: 'white', border: 'none', padding: '14px 32px', borderRadius: '12px', cursor: 'pointer', fontWeight: '600', fontSize: '15px', boxShadow: '0 4px 12px rgba(255, 112, 102, 0.3)' }}>
              🌲 Nieuwe Evaluatie
            </button>
            <button onClick={() => { setResponses({}); setArchetype(null); setCurrentStep('welcome'); }}
              style={{ background: '#012F54', color: 'white', border: 'none', padding: '14px 32px', borderRadius: '12px', cursor: 'pointer', fontWeight: '600', fontSize: '15px' }}>
              ← Terug
            </button>
            {!showResetConfirm ? (
              <button onClick={() => setShowResetConfirm(true)} disabled={loading || !allResults || allResults.totalResponses === 0}
                style={{ background: 'white', color: '#FF7066', border: '2px solid #FF7066', padding: '14px 32px', borderRadius: '12px', cursor: (loading || !allResults || allResults.totalResponses === 0) ? 'not-allowed' : 'pointer', fontWeight: '600', fontSize: '15px', opacity: (loading || !allResults || allResults.totalResponses === 0) ? 0.4 : 1 }}>
                🗑️ Reset Data
              </button>
            ) : (
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <span style={{ fontSize: '13px', color: '#FF7066', fontWeight: '600' }}>Weet je het zeker?</span>
                <button onClick={handleResetData} style={{ background: '#FF7066', color: 'white', border: 'none', padding: '10px 20px', borderRadius: '10px', cursor: 'pointer', fontWeight: '700', fontSize: '14px' }}>Ja, verwijder</button>
                <button onClick={() => setShowResetConfirm(false)} style={{ background: '#e2e8f0', color: '#4a5568', border: 'none', padding: '10px 20px', borderRadius: '10px', cursor: 'pointer', fontWeight: '600', fontSize: '14px' }}>Annuleer</button>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Result Screen
  if (currentStep === 'result' && archetype) {
    const arch = archetypes[archetype];
    return (
      <div style={{ minHeight: '100vh', background: `linear-gradient(135deg, ${arch.color}20 0%, #ffffff 100%)`, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px', fontFamily: 'Arial, sans-serif' }}>
        <div style={{ background: 'white', borderRadius: '20px', padding: '28px 32px', maxWidth: '660px', width: '100%', boxShadow: `0 20px 60px ${arch.color}30`, border: `3px solid ${arch.color}` }}>
          <div style={{ textAlign: 'center', marginBottom: '16px' }}>
            <div style={{ fontSize: '60px', marginBottom: '8px' }}>{arch.icon}</div>
            <h1 style={{ fontSize: '28px', color: '#012F54', marginBottom: '4px', fontWeight: '700' }}>{arch.name}</h1>
            <p style={{ fontSize: '14px', color: arch.color, fontWeight: '700', marginBottom: '2px' }}>{arch.subtitle} — {arch.tagline}</p>
            <p style={{ fontSize: '13px', color: '#4a5568', fontStyle: 'italic' }}>{arch.description}</p>
          </div>

          {arch.kenmerken && (
            <div style={{ display: 'flex', gap: '8px', marginBottom: '14px', flexWrap: 'wrap' }}>
              {arch.kenmerken.map((k, i) => (
                <div key={i} style={{ flex: '1 1 0', minWidth: '120px', background: `${arch.color}10`, borderRadius: '10px', padding: '10px', textAlign: 'center' }}>
                  <p style={{ fontSize: '13px', color: '#012F54', fontWeight: '700', marginBottom: '2px' }}>{k.title}</p>
                  <p style={{ fontSize: '12px', color: '#4a5568', lineHeight: '1.4', margin: 0 }}>{k.text}</p>
                </div>
              ))}
            </div>
          )}

          <div style={{ background: `${arch.color}10`, borderLeft: `4px solid ${arch.color}`, padding: '14px 18px', borderRadius: '10px', marginBottom: '14px' }}>
            <h2 style={{ fontSize: '15px', color: '#012F54', marginBottom: '6px', fontWeight: '700' }}>Jouw AI-Stijl</h2>
            <p style={{ fontSize: '13px', color: '#2d3748', lineHeight: '1.7', marginBottom: '0' }}>{arch.feedback}</p>
          </div>

          <div style={{ background: '#f7fafc', padding: '14px 18px', borderRadius: '10px', marginBottom: '16px' }}>
            <h3 style={{ fontSize: '15px', color: '#012F54', marginBottom: '8px', fontWeight: '700' }}>💡 Jouw Volgende Stap</h3>
            <p style={{ fontSize: '13px', color: '#4a5568', lineHeight: '1.6', marginBottom: '10px' }}>{arch.nextStep}</p>
            {arch.opleidingen && (
              <>
                <p style={{ fontSize: '13px', color: '#012F54', fontWeight: '600', marginBottom: '8px' }}>Vanuit Beeckestijn Business School raden we je aan:</p>
                <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                  {arch.opleidingen.map((opl, i) => (
                    <a key={i} href={opl.url} target="_blank" rel="noopener noreferrer"
                      style={{ background: `${arch.color}20`, color: '#012F54', padding: '6px 12px', borderRadius: '16px', fontSize: '12px', fontWeight: '600', textDecoration: 'none', border: `2px solid ${arch.color}40`, transition: 'all 0.2s', display: 'inline-block' }}
                      onMouseOver={(e) => { e.currentTarget.style.background = arch.color; e.currentTarget.style.color = arch.color === '#88E6FF' ? '#012F54' : 'white'; e.currentTarget.style.border = `2px solid ${arch.color}`; }}
                      onMouseOut={(e) => { e.currentTarget.style.background = `${arch.color}20`; e.currentTarget.style.color = '#012F54'; e.currentTarget.style.border = `2px solid ${arch.color}40`; }}>
                      {opl.title} →
                    </a>
                  ))}
                </div>
              </>
            )}
          </div>

          <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
  <a href="https://www.beeckestijn.org/artificial-intelligence-trainingen/" target="_blank" rel="noopener noreferrer"
    style={{ background: '#FF7066', color: 'white', padding: '12px 24px', fontSize: '14px', borderRadius: '10px', fontWeight: '600', textDecoration: 'none', display: 'inline-block' }}
    onMouseOver={(e) => e.currentTarget.style.background = '#e05a50'}
    onMouseOut={(e) => e.currentTarget.style.background = '#FF7066'}>
    Bekijk onze AI Trainingen & Opleidingen →
  </a>
  <button onClick={() => { setResponses({}); setArchetype(null); setCurrentStep('welcome'); }}
    style={{ background: 'white', color: '#012F54', border: '2px solid #012F54', padding: '12px 24px', fontSize: '14px', borderRadius: '10px', cursor: 'pointer', fontWeight: '600' }}>
    ← Terug naar Start
  </button>
</div>
        </div>
      </div>
    );
  }

  // Question Screens
  if (currentStep.startsWith('question-')) {
    const question = questions[currentQuestionIndex];
    const selectedAnimal = responses[question.id];

    return (
      <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #012F54 0%, #88E6FF 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px', fontFamily: 'Arial, sans-serif' }}>
        <div style={{ background: 'white', borderRadius: '20px', padding: '24px 28px', maxWidth: '620px', width: '100%', boxShadow: '0 20px 60px rgba(1, 47, 84, 0.3)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '18px', gap: '6px' }}>
            {questions.map((_, idx) => (
              <div key={idx} style={{ flex: 1, height: '6px', background: idx <= currentQuestionIndex ? '#012F54' : '#e2e8f0', borderRadius: '4px', transition: 'all 0.3s' }} />
            ))}
          </div>

          <div style={{ marginBottom: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: '13px', color: '#718096', fontWeight: '600' }}>Vraag {currentQuestionIndex + 1} van {questions.length}</span>
            <button onClick={() => { setResponses({}); setArchetype(null); setCurrentStep('welcome'); }}
              style={{ background: 'none', border: 'none', color: '#FF7066', fontSize: '13px', fontWeight: '600', cursor: 'pointer', padding: '4px 8px', borderRadius: '6px' }}
              onMouseOver={(e) => e.target.style.background = '#FFF0EF'}
              onMouseOut={(e) => e.target.style.background = 'none'}>
              ✕ Annuleer
            </button>
          </div>

          <h2 style={{ fontSize: '17px', color: '#012F54', marginBottom: '14px', fontWeight: '700', lineHeight: '1.4' }}>{question.title}</h2>

          <div style={{ marginBottom: '16px' }}>
            {question.options.map((option) => {
              const isSelected = selectedAnimal === option.animal;
              return (
                <button key={option.animal} onClick={() => handleSelect(question.id, option.animal)}
                  style={{ width: '100%', padding: '10px 14px', marginBottom: '7px', background: isSelected ? '#012F54' : 'white', color: isSelected ? 'white' : '#2d3748', border: isSelected ? 'none' : '2px solid #e2e8f0', borderRadius: '10px', fontSize: '13px', cursor: 'pointer', textAlign: 'left', fontWeight: '500', transition: 'all 0.2s', boxShadow: isSelected ? '0 4px 12px rgba(1, 47, 84, 0.3)' : 'none', lineHeight: '1.4' }}
                  onMouseOver={(e) => { if (!isSelected) { e.target.style.borderColor = '#88E6FF'; e.target.style.background = '#f7fafc'; } }}
                  onMouseOut={(e) => { if (!isSelected) { e.target.style.borderColor = '#e2e8f0'; e.target.style.background = 'white'; } }}>
                  {option.label}
                </button>
              );
            })}
          </div>

          <div style={{ display: 'flex', gap: '10px' }}>
            {currentQuestionIndex > 0 && (
              <button onClick={() => setCurrentStep(`question-${currentQuestionIndex - 1}`)}
                style={{ background: 'white', color: '#012F54', border: '2px solid #e2e8f0', padding: '12px 18px', fontSize: '16px', borderRadius: '10px', cursor: 'pointer', fontWeight: '600' }}>
                ←
              </button>
            )}
            <button onClick={() => handleNext(currentQuestionIndex)} disabled={!selectedAnimal}
              style={{ background: selectedAnimal ? '#FF7066' : '#cbd5e0', color: 'white', border: 'none', padding: '12px 32px', fontSize: '14px', borderRadius: '10px', cursor: selectedAnimal ? 'pointer' : 'not-allowed', fontWeight: '600', flex: 1, boxShadow: selectedAnimal ? '0 4px 12px rgba(255, 112, 102, 0.4)' : 'none', transition: 'all 0.2s' }}>
              {currentQuestionIndex === questions.length - 1 ? 'Bekijk Resultaat →' : 'Volgende Vraag →'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default AIEvolutionGame;
